import className from 'classnames/bind';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Image } from '@components';
import { getLinkData, replaceStringVar } from '@/core/utils';
import AccordionIcon from '@/assets/svg/accordion-icon.svg';
import { AccordionFragment } from '@/__generated__/graphql';

import styles from './Accordion.module.scss';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

interface AccordionItemProps {
  content: AccordionFragment['accordions'][number];
  isOpen?: boolean;
  index?: number;
  onToggle?: (id: string) => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  content,
  isOpen = false,
  index,
  onToggle,
}) => {
  const [internalOpen, setInternalOpen] = useState(isOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const open = useRef(internalOpen);
  const [height, setHeight] = useState<number | string>(0);
  const { headline, subhead, body, cta, image } = content;
  const { labels: { ariaLabels } } = usePageWrapperData();

  const isControlled = onToggle !== undefined;
  const id = `accordion-item-${index}`;
  const ctaData = getLinkData(cta);
  const ariaLabel = useMemo(() => replaceStringVar(ariaLabels?.accordionButton, { headline }), [ariaLabels?.accordionButton, headline]);

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.(id);
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  const updateHeight = () => {
    if (contentRef.current) {
      setHeight(open.current ? `calc(${contentRef.current.scrollHeight}px + 3rem)` : 0);
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      open.current = internalOpen;
      setHeight(open.current ? `calc(${contentRef.current.scrollHeight}px + 3rem)` : 0);
    }
  }, [internalOpen]);

  useEffect(() =>{
    if (isControlled) setInternalOpen(isOpen);
  }, [isOpen])

  useEffect(() => {
    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <div className={cx("accordion-item")}>
      <h3>
        <button
          className={cx("accordion-button")}
          aria-expanded={internalOpen}
          aria-controls={`${id}-content`}
          onClick={handleToggle}
          type="button"
        >
          <div className={cx("text")}>
            <span className={cx("title", "title-lc-s")}>{headline}</span>
            {subhead && <span className={cx("subhead", "title-lc-xs")}>{subhead}</span>}
          </div>
          <div className={cx("icon-container", { open: internalOpen })}>
            <AccordionIcon className={cx("icon", "horizontal")} />
            <AccordionIcon className={cx("icon", "vertical")} />
          </div>
        </button>
      </h3>
      <div
        id={`${id}-content`}
        className={cx("content")}
        style={{ height }}
        aria-hidden={!open.current}
      >
        <div ref={contentRef} className={cx("content-inner")}>
          {image && <Image image={image} className={cx("image")} />}
          {body && !image && <div className={cx("body", "rich-text")} dangerouslySetInnerHTML={{ __html: body }} />}
          { ctaData && <Button ariaLabel={ariaLabel} {...ctaData} /> }
        </div>
      </div>
    </div>
  );
};

export default AccordionItem;
